import React, { FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  contactApi,
  PreferredContactChannel,
} from "../api/contactapi";

import serviceContactImage from "../assets/contact/service-contact.png";
import zaloQrImage from "../assets/contact/zalo-qr.png";
import contactPattern from "../assets/contact/form-pattern.svg";

import "../styles/ContactPage.css";

type ContactFormState = {
  fullName: string;
  phone: string;
  email: string;
  need: string;
  preferredContactChannel: PreferredContactChannel;
  message: string;
};

const HOTLINE_DISPLAY = "0901191141";
const HOTLINE_LINK = "0901191141";
const ZALO_URL = `https://zalo.me/3354373045960365130`;

const needOptions = [
  "Tư vấn chọn sản phẩm",
  "Báo giá sản phẩm",
  "Chọn ngọc theo bản mệnh",
  "Hỏi về kiểm định / nguồn gốc",
  "Đặt lịch xem sản phẩm",
  "Liên hệ khác",
];

const contactChannelOptions: {
  value: PreferredContactChannel;
  label: string;
}[] = [
  { value: "zalo", label: "Zalo" },
  { value: "phone", label: "Điện thoại" },
  { value: "email", label: "Email" },
];

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8.2 3.8 5.8 4.9c-.7.3-1 1.1-.8 1.8 1.8 6.1 6.2 10.5 12.3 12.3.7.2 1.5-.1 1.8-.8l1.1-2.4c.3-.7.1-1.5-.6-1.9l-2.7-1.6c-.6-.3-1.3-.2-1.7.3l-1.2 1.5a14.4 14.4 0 0 1-4.1-4.1l1.5-1.2c.5-.4.6-1.1.3-1.7L10.1 4.4c-.4-.7-1.2-.9-1.9-.6Z" />
  </svg>
);

const MessageIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5.5h16v11H9l-5 3v-14Z" />
    <path d="M8 9h8M8 13h5" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
    <path d="M7.5 3.5v4M16.5 3.5v4M3.5 10h17" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m14 7 5 5-5 5" />
  </svg>
);

const ContactPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const productId = searchParams.get("productId") || "";
  const productName = searchParams.get("productName") || "";
  const productSlug = searchParams.get("productSlug") || "";

  const source = productId || productName
    ? "product_detail"
    : "contact_page";

  const [form, setForm] = useState<ContactFormState>({
    fullName: "",
    phone: "",
    email: "",
    need: productName
      ? "Báo giá sản phẩm"
      : "Tư vấn chọn sản phẩm",
    preferredContactChannel: "zalo",
    message: productName
      ? `Tôi muốn được tư vấn và báo giá sản phẩm: ${productName}`
      : "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [sentAt, setSentAt] = useState("");

  const productLink = useMemo(() => {
    if (!productSlug) return "";
    return `/products/${productSlug}`;
  }, [productSlug]);

  const updateField = (
    field: keyof ContactFormState,
    value: string
  ) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!form.fullName.trim()) {
      return "Vui lòng nhập họ tên.";
    }

    if (!form.phone.trim()) {
      return "Vui lòng nhập số điện thoại.";
    }

    const normalizedPhone = form.phone.replace(/\s+/g, "");

    if (!/^[0-9+]{9,15}$/.test(normalizedPhone)) {
      return "Số điện thoại chưa đúng định dạng.";
    }

    if (
      form.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    ) {
      return "Email chưa đúng định dạng.";
    }

    return "";
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const validationMessage = validateForm();

    if (validationMessage) {
      setError(validationMessage);
      setSuccessMessage("");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");
      setSentAt("");

      const data = await contactApi.createContactLead({
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        need: form.need,
        preferredContactChannel:
          form.preferredContactChannel,
        message: form.message.trim(),
        productId,
        productName,
        productSlug,
        source,
      });

      setSuccessMessage(
        data.message ||
          "King Jades đã nhận thông tin. Đội ngũ tư vấn sẽ liên hệ lại sớm."
      );

      if (data.lead?.createdAtUtc) {
        setSentAt(data.lead.createdAtUtc);
      }

      setForm((previous) => ({
        ...previous,
        fullName: "",
        phone: "",
        email: "",
        message: productName
          ? `Tôi muốn được tư vấn và báo giá sản phẩm: ${productName}`
          : "",
      }));
    } catch (requestError) {
      const apiError = requestError as Error;

      setError(
        apiError.message ||
          "Không thể gửi thông tin liên hệ lúc này."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />

      <div className="kj-contact-page">
        <main className="kj-contact-main">
          <section className="kj-contact-hero">
            <div
              className="kj-contact-hero__pattern"
              style={{
                backgroundImage: `url(${contactPattern})`,
              }}
              aria-hidden="true"
            />

            <div
              className="kj-contact-hero__glow kj-contact-hero__glow--left"
              aria-hidden="true"
            />
            <div
              className="kj-contact-hero__glow kj-contact-hero__glow--right"
              aria-hidden="true"
            />

            <div className="kj-contact-shell kj-contact-hero__inner">
              <div className="kj-contact-visual">
                <div className="kj-contact-visual__image">
                  <img
                    src={serviceContactImage}
                    alt="Đội ngũ tư vấn King Jades"
                  />

                  <div className="kj-contact-visual__badge">
                    <span>King Jades Service</span>
                    <strong>Tư vấn riêng 1:1</strong>
                  </div>
                </div>

                <div className="kj-contact-visual__floating-card">
                  <span className="kj-contact-eyebrow">
                    Personalized Guidance
                  </span>

                  <h1>
                    Kết nối để chọn đúng món ngọc dành cho bạn.
                  </h1>

                  <p>
                    Đội ngũ King Jades tiếp nhận nhu cầu về
                    sản phẩm, size vòng, màu sắc, kiểm định và
                    tư vấn theo bản mệnh.
                  </p>

                  <div className="kj-contact-visual__quick-actions">
                    <a href={`tel:${HOTLINE_LINK}`}>
                      <PhoneIcon />
                      Gọi {HOTLINE_DISPLAY}
                    </a>

                    <a
                      href={ZALO_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <MessageIcon />
                      Nhắn Zalo
                    </a>
                  </div>
                </div>
              </div>

              <div className="kj-contact-form-card">
                <div className="kj-contact-form-card__heading">
                  <div>
                    <span className="kj-contact-eyebrow">
                      Contact King Jades
                    </span>
                    <h2>Gửi yêu cầu tư vấn</h2>
                    <p>
                      Điền thông tin bên dưới. Đội ngũ tư vấn sẽ
                      liên hệ theo kênh bạn ưu tiên.
                    </p>
                  </div>

                  {productName && (
                    <div className="kj-contact-product-reference">
                      <span>Sản phẩm quan tâm</span>

                      {productLink ? (
                        <Link to={productLink}>
                          {productName}
                        </Link>
                      ) : (
                        <strong>{productName}</strong>
                      )}
                    </div>
                  )}
                </div>

                {successMessage && (
                  <div className="kj-contact-alert kj-contact-alert--success">
                    <strong>{successMessage}</strong>

                    {sentAt && (
                      <span>
                        Gửi lúc:{" "}
                        {new Date(sentAt).toLocaleString(
                          "vi-VN"
                        )}
                      </span>
                    )}
                  </div>
                )}

                {error && (
                  <div className="kj-contact-alert kj-contact-alert--error">
                    {error}
                  </div>
                )}

                <form
                  className="kj-contact-form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="kj-contact-form__grid">
                    <label className="kj-contact-field">
                      <span>
                        Họ và tên <b>*</b>
                      </span>
                      <input
                        value={form.fullName}
                        onChange={(event) =>
                          updateField(
                            "fullName",
                            event.target.value
                          )
                        }
                        placeholder="Nhập họ tên"
                        autoComplete="name"
                      />
                    </label>

                    <label className="kj-contact-field">
                      <span>
                        Số điện thoại <b>*</b>
                      </span>
                      <input
                        value={form.phone}
                        onChange={(event) =>
                          updateField(
                            "phone",
                            event.target.value
                          )
                        }
                        placeholder="VD: 0918xxxxxx"
                        inputMode="tel"
                        autoComplete="tel"
                      />
                    </label>

                    <label className="kj-contact-field">
                      <span>Email</span>
                      <input
                        value={form.email}
                        onChange={(event) =>
                          updateField(
                            "email",
                            event.target.value
                          )
                        }
                        placeholder="email@example.com"
                        type="email"
                        autoComplete="email"
                      />
                    </label>

                    <label className="kj-contact-field">
                      <span>Nhu cầu tư vấn</span>
                      <select
                        value={form.need}
                        onChange={(event) =>
                          updateField(
                            "need",
                            event.target.value
                          )
                        }
                      >
                        {needOptions.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <fieldset className="kj-contact-channel">
                    <legend>Kênh liên hệ ưu tiên</legend>

                    <div>
                      {contactChannelOptions.map((item) => (
                        <label key={item.value}>
                          <input
                            type="radio"
                            name="preferredContactChannel"
                            value={item.value}
                            checked={
                              form.preferredContactChannel ===
                              item.value
                            }
                            onChange={() =>
                              updateField(
                                "preferredContactChannel",
                                item.value
                              )
                            }
                          />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <label className="kj-contact-field">
                    <span>Nội dung cần tư vấn</span>
                    <textarea
                      value={form.message}
                      onChange={(event) =>
                        updateField(
                          "message",
                          event.target.value
                        )
                      }
                      rows={5}
                      placeholder="Sản phẩm quan tâm, size cổ tay, màu sắc, ngân sách dự kiến hoặc câu hỏi về kiểm định..."
                    />
                  </label>

                  <button
                    type="submit"
                    className="kj-contact-submit"
                    disabled={loading}
                  >
                    {loading
                      ? "Đang gửi..."
                      : "Gửi yêu cầu tư vấn"}
                    {!loading && <ArrowIcon />}
                  </button>

                  <p className="kj-contact-privacy-note">
                    Thông tin được lưu vào hệ thống Contact Lead
                    để Admin/Sale tiếp nhận và theo dõi tiến độ.
                  </p>
                </form>
              </div>
            </div>
          </section>

          <section className="kj-contact-methods">
            <div className="kj-contact-shell">
              <header className="kj-contact-section-heading">
                <div>
                  <span className="kj-contact-eyebrow">
                    Contact Options
                  </span>
                  <h2>Chọn cách kết nối thuận tiện nhất.</h2>
                </div>

                <p>
                  King Jades hỗ trợ tư vấn trực tiếp qua hotline,
                  Zalo hoặc đặt lịch xem sản phẩm.
                </p>
              </header>

              <div className="kj-contact-methods__grid">
                <article>
                  <span className="kj-contact-methods__icon">
                    <PhoneIcon />
                  </span>
                  <small>Hotline</small>
                  <h3>{HOTLINE_DISPLAY}</h3>
                  <p>
                    Trao đổi nhanh về sản phẩm, size và lịch hẹn.
                  </p>
                  <a href={`tel:${HOTLINE_LINK}`}>
                    Gọi ngay <ArrowIcon />
                  </a>
                </article>

                <article>
                  <span className="kj-contact-methods__icon">
                    <MessageIcon />
                  </span>
                  <small>Zalo tư vấn</small>
                  <h3>Nhắn tin trực tiếp</h3>
                  <p>
                    Gửi hình ảnh hoặc sản phẩm đang quan tâm qua
                    Zalo.
                  </p>
                  <a
                    href={ZALO_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Mở Zalo <ArrowIcon />
                  </a>
                </article>

                <article>
                  <span className="kj-contact-methods__icon">
                    <CalendarIcon />
                  </span>
                  <small>Đặt lịch</small>
                  <h3>Xem sản phẩm 1:1</h3>
                  <p>
                    Đặt trước thời gian để đội ngũ chuẩn bị sản
                    phẩm phù hợp.
                  </p>
                  <Link to="/contact">
                    Đặt lịch tư vấn <ArrowIcon />
                  </Link>
                </article>
              </div>
            </div>
          </section>

          <section className="kj-contact-zalo">
            <div className="kj-contact-shell kj-contact-zalo__inner">
              <div>
                <span className="kj-contact-eyebrow">
                  Zalo Official Account
                </span>
                <h2>Quét mã để kết nối nhanh với King Jades.</h2>
                <p>
                  Theo dõi bộ sưu tập mới, gửi yêu cầu tư vấn và
                  nhận hỗ trợ trực tiếp từ đội ngũ King Jades.
                </p>

                <a
                  href={ZALO_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="kj-contact-zalo__button"
                >
                  Kết nối Zalo
                  <ArrowIcon />
                </a>
              </div>

              <div className="kj-contact-zalo__qr">
                <img
                  src={zaloQrImage}
                  alt="QR Zalo King Jades"
                />
                <strong>Quét mã bằng Zalo</strong>
                <small>King Jades Official Account</small>
              </div>
            </div>
          </section>
        </main>
      </div>

      <Footer />
    </>
  );
};

export default ContactPage;